import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import {MenuItem} from 'primeng/api';
import {MenuModule} from 'primeng/menu';

import {LanguageStore} from '@core/i18n';
import {TranslateHelper, TranslatePipe} from '@mydeals/i18n';
import {Avatar} from '@shared/ui/avatar';

const ACCEPT = 'image/jpeg,image/png,image/webp';

/**
 * Circular avatar with an edit affordance. Empty state shows a `pi pi-user` silhouette and
 * a `+` button that opens the file picker directly. With an image present the button shows a
 * pencil and opens a menu: "Reset" when `canReset` (revert staged changes to the stored
 * photo), "Delete" when `canDelete` (a stored photo exists to remove), and "Upload" always.
 * Selecting a file emits `picked`; the menu emits `reset` / `deleted`. Purely presentational:
 * the parent owns staging/preview state.
 */
@Component({
  selector: 'crm-avatar-field',
  imports: [Avatar, MenuModule, TranslatePipe],
  templateUrl: './avatar-field.html',
  styleUrl: './avatar-field.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvatarField {
  private readonly translateHelper = inject(TranslateHelper);
  private readonly languageStore = inject(LanguageStore);

  readonly displayUrl = input<string | null>(null);
  readonly name = input<string>('');
  readonly canReset = input<boolean>(false);
  readonly canDelete = input<boolean>(false);

  readonly picked = output<File>();
  readonly revert = output<void>();
  readonly deleted = output<void>();

  protected readonly accept = ACCEPT;
  protected readonly hasImage = computed(() => !!this.displayUrl());
  private readonly fileInput = viewChild.required<ElementRef<HTMLInputElement>>('fileInput');

  protected readonly menuItems = computed<MenuItem[]>(() => {
    this.languageStore.current();
    const items: MenuItem[] = [];
    if (this.canReset()) {
      items.push({
        label: this.translateHelper.translate('common.avatar.action.reset'),
        icon: 'pi pi-refresh',
        command: () => this.revert.emit(),
      });
    }
    if (this.canDelete()) {
      items.push({
        label: this.translateHelper.translate('common.avatar.action.remove'),
        icon: 'pi pi-trash',
        command: () => this.deleted.emit(),
      });
    }
    items.push({
      label: this.translateHelper.translate('common.avatar.action.upload'),
      icon: 'pi pi-upload',
      command: () => this.openPicker(),
    });
    return items;
  });

  protected openPicker(): void {
    this.fileInput().nativeElement.click();
  }

  protected onFileSelected(input: HTMLInputElement): void {
    const file = input.files?.[0];
    input.value = '';
    if (file) this.picked.emit(file);
  }
}
